<template>
  <div class="pp-settings" :style="{ '--pp-color': companyColor }">
    <h2>Perfil público</h2>

    <p v-if="!page && loading" class="pp-settings__muted">Carregando...</p>
    <div v-else-if="!page" class="pp-settings__box">
      <p class="pp-settings__muted">Não conseguimos carregar agora.</p>
      <button type="button" class="pp-btn" @click="retryLoad">Tentar de novo</button>
    </div>

    <template v-else>
      <div class="pp-settings__box">
        <div class="pp-settings__row">
          <strong>Seu perfil</strong>
          <span v-if="isPublished" class="pp-settings__status pp-settings__status--on">No ar</span>
          <span v-else-if="hasEverPublished" class="pp-settings__status">Fora do ar</span>
          <span v-else class="pp-settings__status">Ainda não publicado</span>
        </div>
        <a
          v-if="isPublished"
          class="pp-settings__link"
          :href="page.url"
          target="_blank"
          rel="noopener noreferrer"
          @click="ppTrack('public_profile_live_link_opened', { source: 'settings' })"
        >
          {{ shortUrl }}
        </a>
        <button type="button" class="pp-btn pp-btn--primary" @click="openEditor">
          {{ hasEverPublished ? "Editar meu perfil público" : "Criar meu perfil público" }}
        </button>
        <button v-if="hasEverPublished" type="button" class="pp-btn" @click="openAnalytics">
          Ver quem visitou (Analytics)
        </button>
      </div>

      <div class="pp-settings__box">
        <div class="pp-settings__row">
          <strong>Plano</strong>
          <pro-badge v-if="isPro" size="md" />
        </div>

        <template v-if="plan.type === 'lifetime' && isPro">
          <p class="pp-settings__plan">PRO para sempre</p>
          <p class="pp-settings__muted">Pagamento único, sem mensalidade.</p>
        </template>

        <template v-else-if="plan.type === 'monthly' && plan.status">
          <p class="pp-settings__plan">PRO mensal</p>
          <p v-if="plan.status === 'past_due'" class="pp-settings__warning">
            Não conseguimos cobrar seu cartão. Atualize o pagamento para não perder o PRO.
          </p>
          <p v-else-if="plan.status === 'canceled' || plan.cancel_at_period_end" class="pp-settings__muted">
            {{
              periodEnd
                ? `Assinatura cancelada. O PRO fica ativo até ${periodEnd}.`
                : "Assinatura cancelada."
            }}
          </p>
          <p v-else-if="periodEnd" class="pp-settings__muted">
            Próxima cobrança em {{ periodEnd }}, de {{ monthlyPrice }}.
          </p>
          <button
            type="button"
            class="pp-btn"
            :disabled="openingPortal"
            @click="openBillingPortal"
          >
            {{ openingPortal ? "Abrindo..." : "Gerenciar assinatura" }}
          </button>
        </template>

        <template v-else>
          <p class="pp-settings__plan">Grátis</p>
          <p class="pp-settings__muted">
            Com o PRO você escolhe o endereço, usa os modelos PRO (Terminal, Jurídico, Finanças e outros) e
            coloca vídeo, áudio e links no perfil.
          </p>
          <button type="button" class="pp-btn pp-btn--pro" @click="openPaywall">
            Conhecer o PRO
          </button>
        </template>
      </div>
    </template>

    <paywall-dialog :active.sync="paywallOpen" source="settings" />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import mixin from "@/components/public-profile/mixin";
import ProBadge from "@/components/public-profile/ProBadge.vue";
import PaywallDialog from "@/components/public-profile/PaywallDialog.vue";
import { displayUrl, formatCents, formatDate } from "@/components/public-profile/constants";
import service, { errorReason, errorStatus } from "@/services/publicProfile";

export default {
  name: "PublicProfileSettings",
  components: { ProBadge, PaywallDialog },
  mixins: [mixin],
  data: () => ({ paywallOpen: false, openingPortal: false }),
  computed: {
    ...mapState("publicProfile", ["loading"]),
    ...mapGetters("publicProfile", [
      "page",
      "plan",
      "prices",
      "isPro",
      "isPublished",
      "hasEverPublished",
    ]),
    shortUrl() {
      return displayUrl(this.page && this.page.url);
    },
    periodEnd() {
      return formatDate(this.plan.current_period_end);
    },
    monthlyPrice() {
      return formatCents(this.prices.monthly_cents);
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      // Fora do editor vale o que esta salvo (a demonstracao nao abre o PRO aqui).
      this.$store
        .dispatch("publicProfile/fetch", { keepDraft: false, peek: true })
        .then(() => this.ppTrack("public_profile_settings_viewed", {}, { setPerson: true }))
        .catch((e) => this.ppTrack("public_profile_settings_load_failed", { status: errorStatus(e), reason: errorReason(e) }));
    },
    retryLoad() {
      this.ppTrack("public_profile_settings_load_retry_clicked");
      this.load();
    },
    openPaywall() {
      this.ppTrack("public_profile_pro_cta_clicked", { cta: "settings_know_pro" });
      this.paywallOpen = true;
    },
    openAnalytics() {
      this.ppTrack("public_profile_analytics_opened", { source: "settings" });
      this.$router.push({
        name: "public-profile-analytics",
        params: { company_label: this.companyLabel },
      });
    },
    openEditor() {
      this.ppTrack("public_profile_editor_clicked", { source: "settings", first_time: !this.hasEverPublished });
      this.$router.push({
        name: "public-profile-editor",
        params: { company_label: this.companyLabel },
        query: this.hasEverPublished ? {} : { modelos: "1" },
      });
    },
    async openBillingPortal() {
      this.openingPortal = true;
      this.ppTrack("public_profile_billing_portal_clicked", { source: "settings" });
      try {
        const { url } = await service.billingPortal(this.companyLabel);
        if (!url) throw new Error("url vazia");
        this.ppTrack("public_profile_billing_portal_opened", { source: "settings" });
        window.location.href = url;
      } catch (e) {
        this.openingPortal = false;
        this.ppTrack("public_profile_billing_portal_failed", {
          source: "settings",
          status: errorStatus(e),
          reason: errorReason(e) || (e && e.message) || null,
        });
        this.ppError("Não conseguimos abrir a assinatura agora. Tente de novo em instantes.");
      }
    },
  },
};
</script>

<style lang="scss">
.pp-settings {
  width: 400px;
  max-width: 100%;
  margin: 24px auto;
  font-family: Roboto, sans-serif;

  h2 {
    margin-bottom: 20px;
    color: #333;
    font-size: 20px;
    font-weight: 600;
  }

  &__box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
    padding: 16px;
    border: 0.5px solid #a8a8a8;
    border-radius: 16px;

    .pp-btn {
      margin-top: 8px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;

    strong {
      color: #333;
      font-size: 16px;
      font-weight: 500;
    }
  }

  &__status {
    padding: 2px 8px;
    border: 0.5px solid #a8a8a8;
    border-radius: 4px;
    background: #ecebeb;
    color: #4f4f4f;
    font-size: 12px;
    font-weight: 500;

    &--on {
      border-color: #86efac;
      background: #dcfce7;
      color: #166534;
    }
  }

  &__link {
    color: #4f4f4f;
    font-size: 14px;
    text-decoration: underline;
    word-break: break-all;
  }

  &__plan {
    color: #333;
    font-size: 18px;
    font-weight: 500;
  }

  &__muted {
    color: #696969;
    font-size: 14px;
    line-height: 1.4;
  }

  &__warning {
    color: #de2348;
    font-size: 14px;
    line-height: 1.4;
  }
}
</style>
